// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable no-prototype-builtins */
import { CustomAppState } from "@bg/App/Store/Reducers";
import LazyImage from "@bg/MobiusOverrides/LazyImage";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import getColor from "@insite/mobius/utilities/getColor";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// eslint-disable-next-line
import SwiperCore, { A11y, Navigation, Pagination, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

SwiperCore.use([Navigation, Pagination, Scrollbar, A11y]);

interface OwnProps {
    productImages: { id: string; imagePath: string }[];
    productId: string;
    productUrl: string;
}

const mapStateToProps = (state: CustomAppState) => ({
    lineImages: state.commonActions["lineImages"],
});

export const imageCarousel = css`
    position: relative;
    height: 139px;
    width: 139px;
    @media (max-width: 481px) {
        width: 100%;
    }
    .swiper-container {
        position: relative;
        overflow: hidden;
        height: 139px;
        width: 139px;
    }
    .swiper-wrapper {
        display: flex;
    }
    .swiper-button-next,
    .swiper-button-prev {
        height: 40px;
        display: flex;
        align-items: center;
        position: absolute;
        background: white;
        opacity: 0.75;
        top: 50%;
        transform: translateY(-50%);
        margin: 0;
        color: ${getColor("secondary")};
        max-width: 22px;
        &:after {
            display: none;
        }
    }
    .swiper-button-next {
        right: 0;
    }
    .swiper-button-prev {
        left: 0;
    }
    .swiper-navigation {
        @media print {
            display: none;
        }
    }
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const QuoteLineImageCarousel: React.FC<Props> = ({ productImages, productId, lineImages, productUrl }) => {
    const [showSpinner, setShowSpinner] = React.useState(false);
    const dispatch = useAppDispatch();

    useEffect(() => {
        const getStyles = async () => {
            // eslint-disable-next-line global-require
            const result = await require("swiper/swiper-bundle.min.css");
        };
        getStyles().then(result => {
            setShowSpinner(false);
        });
    }, []);

    if (!productImages || !productImages.length) {
        return null;
    }
    const onSlideChange = (swiper: SwiperCore) => {
        const images = lineImages ? { ...lineImages } : {};
        if (images?.hasOwnProperty(productId)) {
            images[productId as any] = productImages[swiper.activeIndex];
        } else {
            (images[productId as any] as any) = "";
            images[productId as any] = productImages[swiper.activeIndex];
        }

        dispatch({
            type: "commonActionSetter/SetAction",
            payload: {
                name: "lineImages",
                value: { ...images },
            },
        });
    };

    if (showSpinner) {
        return (
            <StyledWrapper
                css={css`
                    display: flex;
                    height: 139px;
                    width: 139px;
                    justify-content: center;
                    align-items: center;
                `}
            >
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper className={`quote-line-carousel-container ${productId}`} css={imageCarousel}>
            <Swiper
                spaceBetween={0}
                navigation={{
                    nextEl: `.swiper-button-next.swiper-button-next_${productId}`,
                    prevEl: `.swiper-button-prev.swiper-button-prev_${productId}`,
                }}
                slidesPerView={1}
                loop={false}
                className="swiper-carousel"
                noSwipingClass="no-swiping-elements"
                onSlideChange={swiper => onSlideChange(swiper)}
            >
                {productImages.map(src => (
                    <SwiperSlide key={`carouselProduct${productId}${src.id}`}>
                        <Link href={productUrl}>
                            <LazyImage
                                src={src.imagePath}
                                cloudinaryWidth="139"
                                disableLazyLoad={true}
                                width="139px"
                                height="139px"
                                css={css`
                                    img {
                                        max-height: 139px;
                                        object-fit: contain;
                                    }
                                `}
                            />
                        </Link>
                    </SwiperSlide>
                ))}
            </Swiper>
            {productImages.length > 1 && (
                <StyledWrapper className="swiper-navigation">
                    <div className={`swiper-button-prev swiper-button-prev_${productId}`}>
                        <Icon src="ChevronLeft" size={20} color="seconadry.main" />
                    </div>
                    <div className={`swiper-button-next swiper-button-next_${productId}`}>
                        <Icon src="ChevronRight" size={20} color="seconadry.main" />
                    </div>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(QuoteLineImageCarousel);
